// Schedule.jsx - Dynamic Slot Booking + Simple Doctor Dropdown

import React, { useState, useEffect, useCallback } from 'react';
import { DateTime } from "luxon";
import Header from '../Header/Hearder';
import './Schedule.css';
import axiosInstance from "../../lib/axiosInstance";
import { canEditModule, getStoredUser } from "../../lib/permissions";

/** Hour/minute from API (handles "13:00", "13:00:00", etc.) */
function scheduleTimeParts(t, fallback) {
  const fb = (fallback || "17:00").split(":").slice(0, 2).map(Number);
  if (t == null || t === "") return fb;
  const s = String(t).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m) return [Number(m[1]), Number(m[2])];
  return fb;
}

/** Avoid cached GET /schedules responses after a mutation */
function schedulesListParams(base) {
  return { ...base, _ts: Date.now() };
}
function normalizeEntityId(val) {

  if (val == null || val === "") return "";
  if (typeof val === "object") return String(val._id || val.id || "");
  return String(val);
}

function Schedule({ branches = [], selectedBranchId = "", onBranchChange }) {
  // Dynamic state for slot management
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Doctor dropdown state (fetched)
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null); // will hold doctor id or null for All
  const [doctors, setDoctors] = useState([]);
  const [schedules, setSchedules] = useState([]);

  const [showAddSlotModal, setShowAddSlotModal] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    doctorId: "",
    dayOfWeek: null,
    days: [], // allow multi-day selection for recurring schedules
    startTime: "09:00",
    endTime: "17:00",
    durationMinutes: 30,
    isRecurring: true,
  });
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockForm, setBlockForm] = useState({
    doctorId: "",
    scheduleId: "",
    date: "",
    startTime: "",
    endTime: "",
    type: "blocked",
    reason: "",
  });
  const [availability, setAvailability] = useState(null);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [slotActionPopup, setSlotActionPopup] = useState("");
  const [activeView, setActiveView] = useState("Day");
  const [rangeSlots, setRangeSlots] = useState([]);
  const [loadingRange, setLoadingRange] = useState(false);
  const [dayStatusData, setDayStatusData] = useState({ items: [], blocks: [] });
  const currentUser = getStoredUser();
  const canManageSchedule = canEditModule(currentUser, "schedule");

  // fetch doctors
  useEffect(() => {
    let mounted = true;
    if (!selectedBranchId) {
      setDoctors([]);
      return;
    }
    axiosInstance.get("/doctor-branches", { params: { branchId: selectedBranchId, isActive: true } }).then(r => {
      const d = r && r.data ? r.data : r;
      const maps = Array.isArray(d) ? d : (d.items || []);
      const rawDocs = maps.map((m) => m.doctorId).filter(Boolean);
      const seenDocs = new Set();
      const list = [];
      for (const doc of rawDocs) {
        const id = doc && (doc._id || doc.id);
        const key = id != null ? String(id) : "";
        if (!key || seenDocs.has(key)) continue;
        seenDocs.add(key);
        list.push(doc);
      }
      if (mounted) {
        setDoctors(list);
        if (!selectedDoctor && list[0]) {
          setSelectedDoctor(list[0]._id || list[0].id);
        }
      }
    }).catch(() => { }).finally(() => { });
    return () => mounted = false;
  }, [selectedBranchId, selectedDoctor]);

  const fetchDayStatus = useCallback(async (doctorId, date) => {
    try {
      if (!selectedBranchId || !doctorId || !date) return;
      const res = await axiosInstance.get("/slots/day-status", {
        params: { doctorId, branchId: selectedBranchId, date },
      });
      const d = res && res.data ? res.data : res;
      const items = Array.isArray(d?.items) ? d.items : [];
      setDayStatusData(prev => ({ ...prev, items }));
    } catch (err) {
      console.error("fetchDayStatus failed", err);
    }
  }, [selectedBranchId]);

  const fetchBlocks = useCallback(async (doctorId, date) => {
    try {
      if (!selectedBranchId) return;
      const res = await axiosInstance.get("/blocks", { params: { doctorId, branchId: selectedBranchId, date } });
      const data = res && res.data ? res.data : res;
      const items = data && data.items ? data.items : (Array.isArray(data) ? data : []);
      setDayStatusData(prev => ({ ...prev, blocks: items }));
    } catch (err) {
      console.error("fetchBlocks failed", err);
    }
  }, [selectedBranchId]);

  const fetchAvailability = useCallback(async (doctorId, date) => {
    try {
      if (!selectedBranchId) return;
      const params = { date, branchId: selectedBranchId };
      if (doctorId) params.doctorId = doctorId;
      const res = await axiosInstance.get("/schedules/available", { params });
      setAvailability(res && res.data ? res.data : res);
    } catch (err) {
      console.error("fetch availability failed", err);
    }
  }, [selectedBranchId]);

  const extractBookedName = useCallback((item) => {
    if (!item) return "";
    const candidate =
      (item.patient && item.patient.name) ||
      (item.patientId && item.patientId.name) ||
      (item.appointment && item.appointment.patientName) ||
      (item.appointment && item.appointment.patient && item.appointment.patient.name) ||
      (item.appointment && item.appointment.patientId && item.appointment.patientId.name) ||
      (item.appointmentId && item.appointmentId.patientId && item.appointmentId.patientId.name) ||
      item.patientName ||
      item.userName ||
      item.customerName ||
      "";
    return String(candidate || "").trim();
  }, []);

  const extractBookedPhone = useCallback((item) => {
    if (!item) return "";
    const candidate =
      (item.patient && item.patient.phone) ||
      (item.patientId && item.patientId.phone) ||
      (item.appointment && item.appointment.patientPhone) ||
      (item.appointment && item.appointment.patient && item.appointment.patient.phone) ||
      (item.appointment && item.appointment.patientId && item.appointment.patientId.phone) ||
      (item.appointmentId && item.appointmentId.patientId && item.appointmentId.patientId.phone) ||
      item.patientPhone ||
      item.phone ||
      "";
    return String(candidate || "").trim();
  }, []);

  const normalizeAppointmentId = useCallback((value) => {
    if (!value) return "";
    if (typeof value === "object") return String(value._id || value.id || "");
    return String(value);
  }, []);

  useEffect(() => {
    async function loadRangeStatus() {
      if (!selectedBranchId || !selectedDoctor || activeView === "Day") {
        setRangeSlots([]);
        return;
      }
      setLoadingRange(true);
      try {
        const base = DateTime.fromJSDate(selectedDate);
        let from = base;
        let to = base;
        if (activeView === "Week") {
          from = base.startOf("week");
          to = base.endOf("week");
        } else if (activeView === "Month") {
          from = base.startOf("month");
          to = base.endOf("month");
        } else {
          // List view: next 30 days from selected date
          from = base.startOf("day");
          to = base.plus({ days: 30 }).endOf("day");
        }
        const res = await axiosInstance.get("/slots/range-status", {
          params: {
            doctorId: selectedDoctor,
            branchId: selectedBranchId,
            from: from.toISODate(),
            to: to.toISODate(),
          },
        });
        const d = res && res.data ? res.data : res;
        const items = Array.isArray(d?.items) ? d.items : (Array.isArray(d) ? d : []);
        setRangeSlots(items);
      } catch (err) {
        console.error("loadRangeStatus failed", err);
        setRangeSlots([]);
      } finally {
        setLoadingRange(false);
      }
    }
    loadRangeStatus();
  }, [activeView, selectedDate, selectedDoctor, selectedBranchId]);

  // fetch schedules when selectedDoctor or selectedDate changes
  useEffect(() => {
    async function loadSchedules() {
      try {
        if (!selectedBranchId) {
          setSchedules([]);
          setTimeSlots([]);
          return;
        }
        const dateStr = DateTime.fromJSDate(selectedDate).toISODate();
        const params = { branchId: selectedBranchId, date: dateStr };
        if (selectedDoctor) params.doctorId = selectedDoctor;
        const res = await axiosInstance.get("/schedules", { params: schedulesListParams(params) });
        const d = res && res.data ? res.data : res;
        const list = Array.isArray(d) ? d : (d.items || []);
        setSchedules(list);
      } catch (err) {
        console.error("load schedules error", err);
        setSchedules([]);
      }
    }
    loadSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoctor, selectedDate, selectedBranchId]);

  // build timeSlots from schedules
  useEffect(() => {
    const tz = process.env.REACT_APP_CLINIC_TZ || "Asia/Kolkata";
    const dateISO = DateTime.fromJSDate(selectedDate).toISODate();
    const selectedWeekday = DateTime.fromISO(dateISO, { zone: tz }).weekday % 7; // 0=Sun..6=Sat
    const daySchedules = (Array.isArray(schedules) ? schedules : []).filter((s) => {
      if (!s) return false;
      const scheduleDoctorId = normalizeEntityId(s.doctorId);
      if (selectedDoctor) {
        const selectedDoctorId = String(selectedDoctor);
        if (!scheduleDoctorId || scheduleDoctorId !== selectedDoctorId) return false;
      }
      const specific = s.specificDate ? DateTime.fromISO(String(s.specificDate), { setZone: true }).setZone(tz).toISODate() : "";
      if (specific) return specific === dateISO;
      const dow = s.dayOfWeek;
      if (Array.isArray(dow)) {
        return dow.map((d) => Number(d)).includes(selectedWeekday);
      }
      if (dow !== undefined && dow !== null && String(dow) !== "") {
        return Number(dow) === selectedWeekday;
      }
      return false;
    });

    const dedupedSchedulesMap = new Map();
    daySchedules.forEach((s) => {
      const specificDate = s.specificDate
        ? DateTime.fromISO(String(s.specificDate), { setZone: true }).setZone(tz).toISODate()
        : "";
      const isSpecificForSelectedDate = specificDate === dateISO;
      const startKey = String(s.startTime || "09:00");
      const durKey = Number(s.durationMinutes || 30);
      const key = `${normalizeEntityId(s.doctorId)}|${startKey}|${durKey}`;
      const prev = dedupedSchedulesMap.get(key);
      if (!prev) {
        dedupedSchedulesMap.set(key, s);
        return;
      }
      const prevSpecificDate = prev.specificDate
        ? DateTime.fromISO(String(prev.specificDate), { setZone: true }).setZone(tz).toISODate()
        : "";
      const prevIsSpecificForSelectedDate = prevSpecificDate === dateISO;
      if (isSpecificForSelectedDate && !prevIsSpecificForSelectedDate) {
        dedupedSchedulesMap.set(key, s);
        return;
      }
      if (!isSpecificForSelectedDate && prevIsSpecificForSelectedDate) return;
      const prevTs = new Date(prev.updatedAt || prev.createdAt || 0).getTime();
      const curTs = new Date(s.updatedAt || s.createdAt || 0).getTime();
      if (curTs >= prevTs) dedupedSchedulesMap.set(key, s);
    });

    const effectiveSchedules = Array.from(dedupedSchedulesMap.values());
    const slots = [];
    effectiveSchedules.forEach((s, idx) => {
      const dur = Number(s.durationMinutes || 30);
      try {
        const startParts = scheduleTimeParts(s.startTime, "09:00");
        const endParts = scheduleTimeParts(s.endTime, "17:00");
        let cursor = DateTime.fromISO(dateISO, { zone: tz }).set({ hour: startParts[0], minute: startParts[1], second: 0, millisecond: 0 });
        const endDT = DateTime.fromISO(dateISO, { zone: tz }).set({ hour: endParts[0], minute: endParts[1], second: 0, millisecond: 0 });
        let slotId = slots.length + 1;
        while (cursor.plus({ minutes: dur }) <= endDT) {
          slots.push({
            id: `${s._id || s.id || idx}-${slotId}`,
            time: cursor.toFormat("HH:mm"),
            status: s.isActive ? "available" : "blocked",
            durationMinutes: dur,
            scheduleId: s._id || s.id,
            doctorId: s.doctorId,
          });
          cursor = cursor.plus({ minutes: dur });
          slotId++;
        }
      } catch (e) {}
    });

    const uniqueByTime = new Map();
    slots.forEach((slot) => {
      if (!uniqueByTime.has(slot.time)) uniqueByTime.set(slot.time, slot);
    });
    const dedupedSlots = Array.from(uniqueByTime.values()).sort((a, b) => {
      const [ah, am] = String(a.time || "00:00").split(":").map(Number);
      const [bh, bm] = String(b.time || "00:00").split(":").map(Number);
      return ah * 60 + am - (bh * 60 + bm);
    });
    setTimeSlots(dedupedSlots);

    const date = DateTime.fromJSDate(selectedDate).toISODate();
    if (selectedDoctor && selectedBranchId) {
      (async () => {
        try {
          await fetchDayStatus(selectedDoctor, date);
          await fetchBlocks(selectedDoctor, date);
        } catch (e) {}
      })();
    }
  }, [schedules, selectedDate, selectedDoctor, selectedBranchId, fetchDayStatus, fetchBlocks]);

  useEffect(() => {
    const tz = process.env.REACT_APP_CLINIC_TZ || "Asia/Kolkata";
    const dateISO = DateTime.fromJSDate(selectedDate).toISODate();
    const statusByTime = new Map();
    (dayStatusData.items || []).forEach((it) => {
      try {
        const startVal = it.startTime || it.start || it.startISO;
        if (!startVal) return;
        const hhmm = /^\d{2}:\d{2}$/.test(String(startVal))
          ? String(startVal)
          : DateTime.fromISO(String(startVal), { setZone: true }).setZone(tz).toFormat("HH:mm");
        statusByTime.set(hhmm, {
          status: String(it.status || "available").toLowerCase(),
          appointmentId: normalizeAppointmentId(it.appointmentId) || null,
          bookedBy: (extractBookedName(it) + (extractBookedPhone(it) ? ` (${extractBookedPhone(it)})` : "")).trim(),
        });
      } catch (e) {}
    });

    const blockRanges = (dayStatusData.blocks || []).map((ex) => {
      try {
        const startVal = ex.startTime || ex.start || ex.startISO;
        const endVal = ex.endTime || ex.end || ex.endISO;
        if (!startVal) return null;
        const start = /^\d{2}:\d{2}$/.test(String(startVal))
          ? DateTime.fromISO(`${dateISO}T${startVal}`, { zone: tz })
          : DateTime.fromISO(String(startVal), { setZone: true }).setZone(tz);
        const end = endVal
          ? (/^\d{2}:\d{2}$/.test(String(endVal))
            ? DateTime.fromISO(`${dateISO}T${endVal}`, { zone: tz })
            : DateTime.fromISO(String(endVal), { setZone: true }).setZone(tz))
          : start.plus({ minutes: 30 });
        if (!start.isValid || !end.isValid || end <= start) return null;
        return { start, end, reason: ex.reason || ex.type || "blocked", id: ex._id || ex.id };
      } catch (e) { return null; }
    }).filter(Boolean);

    setTimeSlots((prev) => prev.map((slot) => {
      let updated = { ...slot };
      const s = statusByTime.get(slot.time);
      if (s) {
        updated.status = s.status;
        updated.appointmentId = s.appointmentId;
        updated.bookedBy = s.status === "booked" ? s.bookedBy : "";
      }
      try {
        const slotStart = DateTime.fromISO(`${dateISO}T${slot.time}`, { zone: tz });
        const slotEnd = slotStart.plus({ minutes: slot.durationMinutes || 30 });
        const matchedBlocks = blockRanges.filter((r) => r.start < slotEnd && r.end > slotStart);
        if (matchedBlocks.length > 0) {
          updated.status = "blocked";
          updated.blockReasons = matchedBlocks.map((m) => m.reason);
          updated.blockExceptionIds = matchedBlocks.map((m) => m.id);
        } else {
          delete updated.blockReasons;
          delete updated.blockExceptionIds;
        }
      } catch (e) {}
      return updated;
    }));
  }, [dayStatusData, selectedDate, extractBookedName, extractBookedPhone, normalizeAppointmentId]);

  async function submitSchedule() {
    if (!canManageSchedule) return;
    if (!newSchedule.doctorId) {
      alert("Please select a doctor for this schedule.");
      return;
    }
    if (!selectedBranchId) {
      alert("Please select a branch.");
      return;
    }
    if (!newSchedule.startTime || !newSchedule.endTime) {
      alert("Start and end time required.");
      return;
    }
    try {
      const payload = { ...newSchedule, branchId: selectedBranchId };
      if (!payload.isRecurring && !payload.specificDate) {
        alert("Please provide a specific date for non-recurring schedule.");
        return;
      }
      if (editingScheduleId) {
        const upd = { ...payload };
        if (Array.isArray(upd.days) && upd.days.length > 0) {
          upd.dayOfWeek = upd.days;
          delete upd.days;
        }
        await axiosInstance.put(`/schedules/${editingScheduleId}`, upd);
        setEditingScheduleId(null);
      } else {
        if (payload.isRecurring && Array.isArray(payload.days) && payload.days.length > 0) {
          for (const d of payload.days) {
            const p = { ...payload, dayOfWeek: Number(d) };
            delete p.days;
            await axiosInstance.post("/schedules", p);
          }
        } else {
          await axiosInstance.post("/schedules", payload);
        }
      }
      const params = { branchId: selectedBranchId, date: selectedDate.toISOString().slice(0, 10) };
      if (selectedDoctor) params.doctorId = selectedDoctor;
      const res = await axiosInstance.get("/schedules", { params: schedulesListParams(params) });
      const d = res && res.data ? res.data : res;
      const list = Array.isArray(d) ? d : (d.items || []);
      setSchedules(list);
      setShowAddSlotModal(false);
    } catch (err) {
      console.error("create schedule failed", err);
      alert((err && err.response && err.response.data && err.response.data.error) || (err && err.message) || "Create failed");
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const changeDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (activeView === "Month") {
      newDate.setMonth(selectedDate.getMonth() + direction);
    } else if (activeView === "Week") {
      newDate.setDate(selectedDate.getDate() + (7 * direction));
    } else if (activeView === "List") {
      newDate.setDate(selectedDate.getDate() + (30 * direction));
    } else {
      newDate.setDate(selectedDate.getDate() + direction);
    }
    setSelectedDate(newDate);
    setSchedules([]);
    setTimeSlots([]);
    setDayStatusData({ items: [], blocks: [] });
  };

  const selectDoctor = (doctorId) => {
    setSelectedDoctor(doctorId);
    setShowDoctorDropdown(false);
    setSchedules([]);
    setTimeSlots([]);
    setDayStatusData({ items: [], blocks: [] });
  };

  const openSlotModal = (slot) => {
    if (!canManageSchedule) return;
    const latest = timeSlots.find(s => s.id === slot.id) || slot;
    setSelectedSlot(latest);
    setShowModal(true);
  };

  const formatDate = () => {
    if (activeView === "Month") {
      return selectedDate.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      });
    }
    if (activeView === "Week") {
      const start = new Date(selectedDate);
      const day = start.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;
      start.setDate(start.getDate() + diffToMonday);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    if (activeView === "List") {
      const start = new Date(selectedDate);
      const end = new Date(selectedDate);
      end.setDate(end.getDate() + 30);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return selectedDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  };

  const dtSel = DateTime.fromJSDate(selectedDate);
  const yearValue = String(dtSel.year);
  const monthValue = dtSel.toFormat("yyyy-LL");
  const dateValue = dtSel.toISODate();
  const weekValue = `${dtSel.weekYear}-W${String(dtSel.weekNumber).padStart(2, "0")}`;

  function setSelectedDateSafe(nextDate) {
    const d = nextDate instanceof Date ? nextDate : new Date(nextDate);
    if (d && !Number.isNaN(d.getTime())) {
      setSelectedDate(d);
      setSchedules([]);
      setTimeSlots([]);
      setDayStatusData({ items: [], blocks: [] });
    }
  }

  const yearOptions = (() => {
    const y = dtSel.year;
    const list = [];
    for (let i = y - 5; i <= y + 5; i++) list.push(i);
    return list;
  })();

  async function openEditSchedule(scheduleId) {
    try {
      const res = await axiosInstance.get(`/schedules/${scheduleId}`);
      const s = res && res.data ? res.data : res;
      if (!s) {
        alert("Schedule not found");
        return;
      }
      setNewSchedule({
        doctorId: s.doctorId ? (s.doctorId._id || s.doctorId) : s.doctorId,
        dayOfWeek: Array.isArray(s.dayOfWeek) ? s.dayOfWeek[0] : s.dayOfWeek,
        days: Array.isArray(s.dayOfWeek) ? s.dayOfWeek : (s.dayOfWeek !== undefined && s.dayOfWeek !== null ? [s.dayOfWeek] : []),
        startTime: s.startTime || "09:00",
        endTime: s.endTime || "17:00",
        durationMinutes: s.durationMinutes || 30,
        isRecurring: typeof s.isRecurring === "undefined" ? true : !!s.isRecurring,
        specificDate: s.specificDate ? (new Date(s.specificDate)).toISOString().slice(0, 10) : undefined,
      });
      setEditingScheduleId(s._id || s.id);
      setShowAddSlotModal(true);
    } catch (err) {
      console.error("openEditSchedule error", err);
      alert("Failed to load schedule");
    }
  }

  function showSlotActionPopup(message) {
    setSlotActionPopup(message);
    setTimeout(() => setSlotActionPopup(""), 2200);
  }

  async function deleteBlockById(id) {
    if (!canManageSchedule) return;
    if (!window.confirm("Remove this block and re-open the slot?")) return;
    try {
      await axiosInstance.delete(`/blocks/${id}`);
      // refresh blocks and availability and schedules
      const date = DateTime.fromJSDate(selectedDate).toISODate();
      const doctorId = selectedDoctor;
      if (doctorId) await fetchBlocks(doctorId, date);
      await fetchDayStatus(doctorId, date);
      const params = { branchId: selectedBranchId, date };
      if (doctorId) params.doctorId = doctorId;
      const res = await axiosInstance.get("/schedules", { params: schedulesListParams(params) });
      const d = res && res.data ? res.data : res;
      const list = Array.isArray(d) ? d : (d.items || []);
      setSchedules(list);
      showSlotActionPopup("Slot unblocked successfully.");
    } catch (err) {
      console.error("delete block failed", err);
      alert("Failed to remove block");
    }
  }

  return (
    <div className="schedule-container">
      <Header branches={branches} selectedBranchId={selectedBranchId} onBranchChange={onBranchChange} />

      {/* MAIN 2-COLUMN LAYOUT */}
      <div className="calendar-main-layout">
        {/* LEFT SIDE: Header + Time Sidebar + Calendar Main */}
        <div className="left-panel">
          {/* Calendar Header on LEFT TOP */}
          <div className="calendar-header1">
            <span className="header-title">Calendar</span>
            <div className="header-tabs">
              <button className={`tab ${activeView === "Day" ? "active" : ""}`} onClick={() => setActiveView("Day")}>Day</button>
              <button className={`tab ${activeView === "Week" ? "active" : ""}`} onClick={() => setActiveView("Week")}>Week</button>
              <button className={`tab ${activeView === "Month" ? "active" : ""}`} onClick={() => setActiveView("Month")}>Month</button>
              <button className={`tab ${activeView === "List" ? "active" : ""}`} onClick={() => setActiveView("List")}>List</button>
            </div>
            <div className="header-controls">
              {/* NEW: Simple Doctor Dropdown */}
              <div className='doctor-select-wrapper'>
                <div className="doctor-select-container">
                  <button
                    className="doctor-select-btn"
                    onClick={() => setShowDoctorDropdown(!showDoctorDropdown)}
                  >
                    <span>{(() => {
                      if (!selectedDoctor) return 'All Doctors';
                      const sd = doctors.find(d => (d._id || d.id) === selectedDoctor);
                      return sd ? sd.name : 'Selected';
                    })()}</span>
                    <svg className="dropdown-arrow" viewBox="0 0 24 24" fill="none">
                      <path d="M7 10l5 5 5-5z" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </button>

                  {showDoctorDropdown && (
                    <div className="doctor-dropdown">
                      <div
                        className="doctor-dropdown-item"
                        onClick={() => selectDoctor(null)}
                      >
                        All Doctors
                      </div>
                      {doctors.map((doctor) => (
                        <div
                          key={doctor._id || doctor.id}
                          className="doctor-dropdown-item"
                          onClick={() => selectDoctor(doctor._id || doctor.id)}
                        >
                          {doctor.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {canManageSchedule && (
                <button className="add-slot-btn" onClick={() => {
                  setNewSchedule({ ...newSchedule, doctorId: selectedDoctor || (doctors[0] && (doctors[0]._id || doctors[0].id)) });
                  setShowAddSlotModal(true);
                }}>+ Add Slot</button>
              )}
            </div>
          </div>

          {/* LEFT BOTTOM: Time Sidebar + Calendar Main - UNCHANGED */}
          <div className="calendar-left-content">
            <div className="time-sidebar">
              <span className="time-sidebar-title">Time Slots</span>
              {timeSlots.map(slot => (
                <div
                  key={slot.id}
                  className={`time-slot ${slot.status}`}
                  onClick={canManageSchedule ? () => openSlotModal(slot) : undefined}
                >
                  <span className="time-label">{slot.time}</span>
                  {slot.status === 'available' && (
                    <div className="availability-badge">
                      {slot.slotsOpen}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="calendar-main">
              <div className="date-header">
                <div className="date-nav">
                  <button className="nav-btn" onClick={() => changeDate(-1)}>‹</button>
                  <h2>{formatDate()}</h2>
                  <button className="nav-btn" onClick={() => changeDate(1)}>›</button>
                </div>
                <div className="date-controls">
                  {/* Jump filters */}
                  <select
                    value={yearValue}
                    onChange={(e) => {
                      const y = Number(e.target.value);
                      if (!y) return;
                      const next = DateTime.fromJSDate(selectedDate).set({ year: y });
                      setSelectedDateSafe(next.toJSDate());
                    }}
                    className="jump-select"
                    title="Jump to year"
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={String(y)}>{y}</option>
                    ))}
                  </select>

                  {activeView === "Month" ? (
                    <input
                      type="month"
                      value={monthValue}
                      className="jump-input"
                      title="Jump to month"
                      onChange={(e) => {
                        const val = String(e.target.value || "").trim(); // yyyy-mm
                        if (!/^\d{4}-\d{2}$/.test(val)) return;
                        setSelectedDateSafe(DateTime.fromISO(`${val}-01`).toJSDate());
                      }}
                    />
                  ) : activeView === "Week" ? (
                    <input
                      type="week"
                      value={weekValue}
                      className="jump-input"
                      title="Jump to week"
                      onChange={(e) => {
                        const val = String(e.target.value || "").trim(); // yyyy-Www
                        const m = val.match(/^(\d{4})-W(\d{2})$/);
                        if (!m) return;
                        const weekYear = Number(m[1]);
                        const weekNumber = Number(m[2]);
                        setSelectedDateSafe(DateTime.fromObject({ weekYear, weekNumber, weekday: 1 }).toJSDate());
                      }}
                    />
                  ) : (
                    <input
                      type="date"
                      value={dateValue}
                      className="jump-input"
                      title="Jump to date"
                      onChange={(e) => {
                        const val = String(e.target.value || "").trim(); // yyyy-mm-dd
                        if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) return;
                        setSelectedDateSafe(new Date(`${val}T00:00:00`));
                      }}
                    />
                  )}

                  <span className="current-time">
                    {currentDate.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                  <button className="today-btn-small" onClick={() => setSelectedDate(new Date())}>
                    Today
                  </button>
                </div>
              </div>

              {activeView === "Day" ? (
                <div className="calendar-grid">
                  {timeSlots.map(slot => (
                    <div
                      key={slot.id}
                      className={`calendar-slot ${slot.status}-slot`}
                      onClick={canManageSchedule ? () => openSlotModal(slot) : undefined}
                    >
                      {slot.status === 'available' ? (
                        <div className="availability-info">
                          {canManageSchedule ? <span>•••</span> : <span>Available</span>}
                          <div className="available-slots">
                            {slot.slotsOpen}
                          </div>
                        </div>
                      ) : slot.status === 'booked' ? (
                        <>
                          {slot.bookedBy ? (
                            <div className="booked-user-info">
                              {slot.bookedBy}
                            </div>
                          ) : null}
                          <div className="booked-indicator">
                            Slot booked
                          </div>
                        </>
                      ) : (
                        <div className="blocked-indicator">
                          Slot blocked
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="range-list">
                  {loadingRange ? (
                    <div className="range-row">Loading slots...</div>
                  ) : rangeSlots.length === 0 ? (
                    <div className="range-row">No slots found in selected range.</div>
                  ) : (
                    rangeSlots
                      .sort((a, b) => new Date(a.startTime || a.start || a.startISO) - new Date(b.startTime || b.start || b.startISO))
                      .map((s, idx) => {
                        const tz = process.env.REACT_APP_CLINIC_TZ || "Asia/Kolkata";
                        const startVal = s.startTime || s.start || s.startISO;
                        const endVal = s.endTime || s.end || s.endISO;
                        const start = startVal ? DateTime.fromISO(String(startVal), { setZone: true }).setZone(tz) : null;
                        const end = endVal ? DateTime.fromISO(String(endVal), { setZone: true }).setZone(tz) : null;
                        const normalizedStatus = String(s.status || "available").toLowerCase();
                        return (
                          <div key={s.slotId || `${startVal}-${idx}`} className="range-row">
                            <div>{start ? start.toFormat("dd LLL yyyy") : "-"}</div>
                            <div>{start ? start.toFormat("hh:mm a") : "-"}{end ? ` - ${end.toFormat("hh:mm a")}` : ""}</div>
                            <div className={`range-status ${normalizedStatus}`}>
                              {normalizedStatus.toUpperCase()}
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT SIDE: Pending Sidebar - UNCHANGED */}
        {/* <div className="right-panel">
                    <div className="pending-sidebar">
                        <div className="pending-header">
                            <h4>Pending Approvals</h4>
                            <div className="pending-count">3</div>
                        </div>
                        <div className="pending-list">
                            <div className="pending-item">
                                <div className="pending-item-top">
                                    <div className="pending-avatar purple">JS</div>
                                    <div className="pending-info">
                                        <div className="pending-name">John Smith</div>
                                        <div className="booking-type">Web Booking</div>
                                    </div>
                                    <div className="time-ago">2m ago</div>
                                </div>
                                <div className="pending-actions">
                                    <button className="action-btn approve">✓ Approve</button>
                                    <button className="action-btn reschedule">📅 Reschedule</button>
                                    <button className="action-btn decline">✕ Decline</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div> */}
      </div>

      {/* SLOT MANAGEMENT MODAL - UNCHANGED */}
      {/* ADD SCHEDULE MODAL */}
      {canManageSchedule && showAddSlotModal && (
        <div className="modal-overlay" onClick={() => setShowAddSlotModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingScheduleId ? "Edit schedule" : "Create schedule"}</h3>
            <div className="form-grid">
              <label>Doctor</label>
              <select value={newSchedule.doctorId || ""} onChange={(e) => setNewSchedule({ ...newSchedule, doctorId: e.target.value })}>
                <option value="">Select doctor</option>
                {doctors.map(d => <option key={d._id || d.id} value={d._id || d.id}>{d.name}</option>)}
              </select>
              <label>Day(s)</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <label style={{ fontWeight: 600 }}><input type="checkbox" checked={newSchedule.days.includes(1)} onChange={(e) => {
                    const days = new Set(newSchedule.days);
                    if (e.target.checked) days.add(1); else days.delete(1);
                    setNewSchedule({ ...newSchedule, days: Array.from(days).sort() });
                  }} /> Mon</label>
                  <label style={{ fontWeight: 600 }}><input type="checkbox" checked={newSchedule.days.includes(2)} onChange={(e) => {
                    const days = new Set(newSchedule.days);
                    if (e.target.checked) days.add(2); else days.delete(2);
                    setNewSchedule({ ...newSchedule, days: Array.from(days).sort() });
                  }} /> Tue</label>
                  <label style={{ fontWeight: 600 }}><input type="checkbox" checked={newSchedule.days.includes(3)} onChange={(e) => {
                    const days = new Set(newSchedule.days);
                    if (e.target.checked) days.add(3); else days.delete(3);
                    setNewSchedule({ ...newSchedule, days: Array.from(days).sort() });
                  }} /> Wed</label>
                  <label style={{ fontWeight: 600 }}><input type="checkbox" checked={newSchedule.days.includes(4)} onChange={(e) => {
                    const days = new Set(newSchedule.days);
                    if (e.target.checked) days.add(4); else days.delete(4);
                    setNewSchedule({ ...newSchedule, days: Array.from(days).sort() });
                  }} /> Thu</label>
                  <label style={{ fontWeight: 600 }}><input type="checkbox" checked={newSchedule.days.includes(5)} onChange={(e) => {
                    const days = new Set(newSchedule.days);
                    if (e.target.checked) days.add(5); else days.delete(5);
                    setNewSchedule({ ...newSchedule, days: Array.from(days).sort() });
                  }} /> Fri</label>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <label style={{ fontWeight: 600 }}><input type="checkbox" checked={newSchedule.days.includes(6)} onChange={(e) => {
                    const days = new Set(newSchedule.days);
                    if (e.target.checked) days.add(6); else days.delete(6);
                    setNewSchedule({ ...newSchedule, days: Array.from(days).sort() });
                  }} /> Sat</label>
                  <label style={{ fontWeight: 600 }}><input type="checkbox" checked={newSchedule.days.includes(0)} onChange={(e) => {
                    const days = new Set(newSchedule.days);
                    if (e.target.checked) days.add(0); else days.delete(0);
                    setNewSchedule({ ...newSchedule, days: Array.from(days).sort() });
                  }} /> Sun</label>
                </div>
                <button type="button" onClick={() => setNewSchedule({ ...newSchedule, days: [1, 2, 3, 4, 5] })} style={{ marginLeft: 8, padding: "6px 10px", borderRadius: 8, border: "1px solid #e6e9ee", background: "#fff" }}>Mon–Fri</button>
              </div>
              <label>Start time</label>
              <input type="time" value={newSchedule.startTime} onChange={(e) => setNewSchedule({ ...newSchedule, startTime: e.target.value })} />
              <label>End time</label>
              <input type="time" value={newSchedule.endTime} onChange={(e) => setNewSchedule({ ...newSchedule, endTime: e.target.value })} />
              <label>Duration (minutes)</label>
              <input type="number" min="5" value={newSchedule.durationMinutes} onChange={(e) => setNewSchedule({ ...newSchedule, durationMinutes: Number(e.target.value) })} />
              <label>Recurring</label>
              <div>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><input type="checkbox" checked={!!newSchedule.isRecurring} onChange={(e) => setNewSchedule({ ...newSchedule, isRecurring: e.target.checked })} /> Recurring</label>
              </div>
              {!newSchedule.isRecurring && <>
                <label>Specific date</label>
                <input type="date" value={newSchedule.specificDate || ""} onChange={(e) => setNewSchedule({ ...newSchedule, specificDate: e.target.value })} />
              </>}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button className="cancel-btn" onClick={() => setShowAddSlotModal(false)}>Cancel</button>
              <button className="create-btn" onClick={submitSchedule}>{editingScheduleId ? "Save changes" : "Create"}</button>
            </div>
          </div>
        </div>
      )}
      {/* BLOCK / EXCEPTION MODAL */}
      {canManageSchedule && showBlockModal && (
        <div className="modal-overlay" onClick={() => setShowBlockModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create exception / block</h3>
            <div className="form-grid">
              <label>Doctor</label>
              <select value={blockForm.doctorId} onChange={(e) => setBlockForm({ ...blockForm, doctorId: e.target.value })}>
                <option value="">Select doctor</option>
                {doctors.map(d => <option key={d._id || d.id} value={d._id || d.id}>{d.name}</option>)}
              </select>
              <label>Date</label>
              <input type="date" value={blockForm.date} onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })} />
              <label>Start time</label>
              <input type="time" value={blockForm.startTime} onChange={(e) => setBlockForm({ ...blockForm, startTime: e.target.value })} />
              <label>End time</label>
              <input type="time" value={blockForm.endTime} onChange={(e) => setBlockForm({ ...blockForm, endTime: e.target.value })} />
              <label>Reason</label>
              <input value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })} />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button className="cancel-btn" onClick={() => setShowBlockModal(false)}>Cancel</button>
              <button className="create-btn" onClick={async () => {
                try {
                  const payload = { ...blockForm, branchId: selectedBranchId };
                  // ensure required
                  if (!payload.branchId || !payload.doctorId || !payload.date || !payload.startTime || !payload.endTime) {
                    alert("branch, doctor, date, start and end required");
                    return;
                  }
                  await axiosInstance.post("/blocks", payload);
                  setShowBlockModal(false);
                  // refresh schedules & availability
                  const dateISO = DateTime.fromJSDate(selectedDate).toISODate();
                  const params = { branchId: selectedBranchId, date: dateISO };
                  if (selectedDoctor) params.doctorId = selectedDoctor;
                  const res = await axiosInstance.get("/schedules", { params: schedulesListParams(params) });
                  const d = res && res.data ? res.data : res;
                  const list = Array.isArray(d) ? d : (d.items || []);
                  setSchedules(list);
                  // fetch availability
                  const targetDoctorId = payload.doctorId || selectedDoctor;
                  const targetDate = payload.date || dateISO;
                  if (targetDoctorId) {
                    await fetchBlocks(targetDoctorId, targetDate);
                  }
                  await fetchDayStatus(targetDoctorId, targetDate);
                  await fetchAvailability(targetDoctorId, targetDate);
                  showSlotActionPopup(
                    `Slot blocked: ${payload.startTime} - ${payload.endTime}${payload.reason ? ` (${payload.reason})` : ""}`
                  );
                } catch (err) {
                  console.error("create block failed", err);
                  alert((err && err.response && err.response.data && err.response.data.error) || (err && err.message) || "Create failed");
                }
              }}>Create block</button>
            </div>
          </div>
        </div>
      )}
      {canManageSchedule && showModal && selectedSlot && (
        <div className="slot-modal-overlay">
          <div className="slot-modal">
            <h3>Manage {selectedSlot.time}</h3>
            <div className="slot-options">
              <button className="slot-option blocked" onClick={() => {
                // Open block modal prefilled with slot info
                const tz = process.env.REACT_APP_CLINIC_TZ || "Asia/Kolkata";
                const dateISO = selectedDate.toISOString().slice(0, 10);
                const schedule = schedules.find(s => (s._id || s.id) === selectedSlot.scheduleId);
                const dur = schedule ? Number(schedule.durationMinutes || 30) : 30;
                const start = selectedSlot.time; // "HH:mm"
                const [sh, sm] = start.split(":").map(Number);
                const end = DateTime.fromISO(dateISO, { zone: tz }).set({ hour: sh, minute: sm }).plus({ minutes: dur }).toFormat("HH:mm");
                setBlockForm({
                  doctorId: selectedSlot.doctorId || selectedDoctor || "",
                  scheduleId: selectedSlot.scheduleId || "",
                  date: dateISO,
                  startTime: start,
                  endTime: end,
                  type: "blocked",
                  reason: "",
                });
                setShowModal(false);
                setShowBlockModal(true);
              }}>
                🔒 Block Slot
              </button>
              {selectedSlot && selectedSlot.scheduleId && (
                <>
                  <button className="slot-option available" onClick={() => {
                    // edit schedule
                    openEditSchedule(selectedSlot.scheduleId);
                    setShowModal(false);
                  }}>
                    ✏️ Edit schedule
                  </button>
                  {/* Unblock if this slot has exception ids */}
                  {selectedSlot.blockExceptionIds && selectedSlot.blockExceptionIds.length > 0 && (
                    <button className="slot-option available" onClick={async () => {
                      // remove first exception id (unblock)
                      const exId = selectedSlot.blockExceptionIds[0];
                      await deleteBlockById(exId);
                      setShowModal(false);
                    }}>
                      🔓 Unblock
                    </button>
                  )}
                </>
              )}
            </div>
            <button className="modal-close" onClick={() => setShowModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {slotActionPopup && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 2500,
            background: "#ffffff",
            color: "#0f172a",
            border: "1px solid #d1d5db",
            borderRadius: 10,
            padding: "10px 14px",
            boxShadow: "0 8px 20px rgba(2,6,23,0.15)",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {slotActionPopup}
        </div>
      )}
      {/* AVAILABILITY MODAL */}
      {showAvailabilityModal && availability && (
        <div className="modal-overlay" onClick={() => setShowAvailabilityModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Availability for {availability && availability.date}</h3>
            <div style={{ display: "grid", gap: 12, maxHeight: '60vh', overflowY: 'auto' }}>
              <div>
                <h4>Available slots</h4>
                {Array.isArray(availability.availableSlots) && availability.availableSlots.length ? (
                  <ul>
                    {availability.availableSlots.map((s, i) => (
                      <li key={i}>
                        {(() => {
                          const start = s.startISO || s.start || s.startTime;
                          const end = s.endISO || s.end || s.endTime;
                          if (start && end) {
                            const sTime = /^\d{2}:\d{2}$/.test(String(start)) ? start : DateTime.fromISO(String(start)).toLocaleString(DateTime.TIME_SIMPLE);
                            const eTime = /^\d{2}:\d{2}$/.test(String(end)) ? end : DateTime.fromISO(String(end)).toLocaleString(DateTime.TIME_SIMPLE);
                            return `${sTime} - ${eTime}`;
                          }
                          return JSON.stringify(s);
                        })()}
                      </li>
                    ))}
                  </ul>
                ) : <div>No available slots</div>}
              </div>
              <div>
                <h4>Removed / blocked slots</h4>
                {Array.isArray(availability.removedSlots) && availability.removedSlots.length ? (
                  <ul>
                    {availability.removedSlots.map((r, i) => (
                      <li key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", padding: "6px 0" }}>
                        <div>
                          {(() => {
                            const start = r.startISO || r.start || r.startTime;
                            const end = r.endISO || r.end || r.endTime;
                            if (start) {
                              const sTime = /^\d{2}:\d{2}$/.test(String(start)) ? start : DateTime.fromISO(String(start)).toLocaleString(DateTime.TIME_SIMPLE);
                              const eTime = end
                                ? (/^\d{2}:\d{2}$/.test(String(end)) ? end : DateTime.fromISO(String(end)).toLocaleString(DateTime.TIME_SIMPLE))
                                : "";
                              return `${sTime}${eTime ? ` - ${eTime}` : ""} — ${r.reason || r.type || "blocked"}`;
                            }
                            return `${JSON.stringify(r)} — ${r.reason || r.type || "blocked"}`;
                          })()}
                        </div>
                        <div>
                          <button type="button" className="small-btn" onClick={() => deleteBlockById(r._id || r.id)}>Unblock</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : <div>None</div>}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <button className="cancel-btn" onClick={() => setShowAvailabilityModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Schedule;
